import { useState, useEffect, useCallback, useRef } from "react";

interface ExtendedDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  mozFullScreenEnabled?: boolean;
  msFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozCancelFullScreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

interface ExtendedHTMLElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface UseFullscreenOptions {
  onEnter?: () => void;
  onExit?: () => void;
  onError?: (error: Error) => void;
}

interface UseFullscreenReturn {
  isFullscreen: boolean;
  enterFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
  toggleFullscreen: () => Promise<void>;
  isSupported: boolean;
}

export const useFullscreen = (
  elementRef?: React.RefObject<HTMLElement>,
  options: UseFullscreenOptions = {},
): UseFullscreenReturn => {
  const [isFullscreen, setIsFullscreen] = useState(() => {
    const extendedDoc = document as ExtendedDocument;
    return !!(
      document.fullscreenElement ||
      extendedDoc.webkitFullscreenElement ||
      extendedDoc.mozFullScreenElement ||
      extendedDoc.msFullscreenElement
    );
  });

  const { onEnter, onExit, onError } = options;

  const targetElementRef = useRef<HTMLElement | null>(null);

  const extendedDoc = document as ExtendedDocument;
  const isSupported = !!(
    document.fullscreenEnabled ||
    extendedDoc.webkitFullscreenEnabled ||
    extendedDoc.mozFullScreenEnabled ||
    extendedDoc.msFullscreenEnabled
  );

  const checkFullscreenStatus = useCallback(() => {
    const extendedDoc = document as ExtendedDocument;
    const fullscreenElement =
      document.fullscreenElement ||
      extendedDoc.webkitFullscreenElement ||
      extendedDoc.mozFullScreenElement ||
      extendedDoc.msFullscreenElement;

    const newIsFullscreen = !!fullscreenElement;

    if (newIsFullscreen !== isFullscreen) {
      setIsFullscreen(newIsFullscreen);

      if (newIsFullscreen) {
        onEnter?.();
      } else {
        onExit?.();
      }
    }
  }, [isFullscreen, onEnter, onExit]);

  const enterFullscreen = useCallback(async () => {
    if (!isSupported) {
      const error = new Error("Fullscreen is not supported");
      onError?.(error);
      throw error;
    }

    try {
      const element =
        elementRef?.current ||
        targetElementRef.current ||
        document.documentElement;
      const extendedElement = element as ExtendedHTMLElement;

      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (extendedElement.webkitRequestFullscreen) {
        await extendedElement.webkitRequestFullscreen();
      } else if (extendedElement.mozRequestFullScreen) {
        await extendedElement.mozRequestFullScreen();
      } else if (extendedElement.msRequestFullscreen) {
        await extendedElement.msRequestFullscreen();
      }
    } catch (error) {
      onError?.(error as Error);
      throw error;
    }
  }, [elementRef, isSupported, onError]);

  const exitFullscreen = useCallback(async () => {
    if (!isSupported) {
      const error = new Error("Fullscreen is not supported");
      onError?.(error);
      throw error;
    }

    try {
      const extendedDoc = document as ExtendedDocument;
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (extendedDoc.webkitExitFullscreen) {
        await extendedDoc.webkitExitFullscreen();
      } else if (extendedDoc.mozCancelFullScreen) {
        await extendedDoc.mozCancelFullScreen();
      } else if (extendedDoc.msExitFullscreen) {
        await extendedDoc.msExitFullscreen();
      }
    } catch (error) {
      onError?.(error as Error);
      throw error;
    }
  }, [isSupported, onError]);

  const toggleFullscreen = useCallback(async () => {
    const extendedDoc = document as ExtendedDocument;
    const currentlyFullscreen = !!(
      document.fullscreenElement ||
      extendedDoc.webkitFullscreenElement ||
      extendedDoc.mozFullScreenElement ||
      extendedDoc.msFullscreenElement
    );

    if (currentlyFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  }, [enterFullscreen, exitFullscreen]);

  useEffect(() => {
    if (!isSupported) return;

    if (elementRef?.current) {
      targetElementRef.current = elementRef.current;
    }

    const events = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];

    events.forEach((event) => {
      document.addEventListener(event, checkFullscreenStatus);
    });

    checkFullscreenStatus();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, checkFullscreenStatus);
      });
    };
  }, [checkFullscreenStatus, elementRef, isSupported]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFullscreen) {
        exitFullscreen().catch(console.error);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, exitFullscreen]);

  return {
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    isSupported,
  };
};
